'use client';

import { useMemo } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import type { RealtimeVoiceDelegationTarget } from '@/lib/realtime-voice-agent';
import { createRealtimeVoiceThreadDispatch } from '@/lib/realtime-voice-thread-dispatch';
import { createYourImaiVoiceDispatch } from '@/lib/your-imai-voice-dispatch';
import { sendYourImaiVoice } from '@/components/voice/your-imai-voice-api';

/** Dispatches live voice work to its original chat without a mounted chat page. */
export function useRealtimeVoiceThreadDispatch({
  orgThreadId,
  orgId,
  sourceKind,
}: {
  orgThreadId: Id<'orgThreads'>;
  orgId: Id<'organizations'>;
  sourceKind?: 'your-imai';
}): RealtimeVoiceDelegationTarget {
  const thread = useQuery(api.organizations.threads.queries.getById, {
    id: orgThreadId,
  });
  const send = useMutation(
    api.products.ai.mutations.org_thread_send_message.sendMessage
  );
  const enqueue = useMutation(
    api.organizations.threads.mutations.enqueueMessage
  );
  const deliverYourImai = useMutation(sendYourImaiVoice);
  const stopStreaming = useAction(
    api.products.ai.actions.org_thread_actions.stopStreaming
  );

  return useMemo(() => {
    const stop = async ({
      expectedPromptMessageId,
      expectedWorkflowId,
      voiceSessionId,
    }: {
      expectedPromptMessageId: string;
      expectedWorkflowId: string;
      voiceSessionId?: string;
    }) => {
      const result = await stopStreaming({
        orgThreadId,
        expectedPromptMessageId,
        expectedWorkflowId,
        voiceSessionId,
        requestSource: 'org_chat_composer',
        reportSubAgentCancellation: true,
      });
      return result?.turnStop === 'stopped';
    };
    if (sourceKind === 'your-imai')
      return createYourImaiVoiceDispatch({
        orgThreadId,
        organizationId: orgId,
        getThread: () => thread,
        deliver: deliverYourImai,
        stop: async (options) => {
          if (!options?.realtimeVoiceSessionId)
            throw new Error('Voice session is unavailable');
          return thread?.activePromptMessageId &&
            thread.activeResponseWorkflowId
            ? stop({
                expectedPromptMessageId: thread.activePromptMessageId,
                expectedWorkflowId: thread.activeResponseWorkflowId,
                voiceSessionId: options.realtimeVoiceSessionId,
              })
            : false;
        },
      });
    return createRealtimeVoiceThreadDispatch({
      orgThreadId,
      orgId,
      getThread: () => thread,
      send,
      enqueue,
      stop,
    });
  }, [
    orgThreadId,
    orgId,
    thread,
    send,
    enqueue,
    stopStreaming,
    sourceKind,
    deliverYourImai,
  ]);
}
